import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, List, LayoutGrid, Route, CalendarClock, Lightbulb, Pencil, Trash2, Archive, ArchiveRestore, AlertTriangle, Search, Clock, PoundSterling, User, CalendarDays, RefreshCw, X, CalendarClock as CalIcon } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { INN_STATUS, INN_HORIZON, INN_PRIORITY, INN_OWNERS, INN_ROADMAP_STATUSES, INN_REVIEW_OUTCOMES, innovationFields, innPriorityBadge, innStatusBadge, innStatusAccent, innHorizonAccent, innHorizonBlurb, isReviewOverdue, isReviewSoon } from "@/data/innovationOptions";

const today = () => new Date().toISOString().slice(0, 10);
const soonDate = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");
const PRIO_ORDER = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const HOR_ORDER = Object.fromEntries(INN_HORIZON.map((h, i) => [h, i]));

const VIEWS = [["all", "All Ideas", List], ["kanban", "Kanban", LayoutGrid], ["roadmap", "Roadmap", Route], ["reviewq", "Review Queue", CalendarClock]];

const Sel = ({ testid, value, onChange, label, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-9 w-auto min-w-[130px] border-[#E4E9F3] text-sm"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent><SelectItem value="all">{label}: All</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const InnovationsPanel = ({ onChange, initialView, initialStatus, navKey, onClearStatus }) => {
  const { canWrite } = useUser();
  const [items, setItems] = useState([]);
  const [view, setView] = useState(initialView || "all");
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [prioF, setPrioF] = useState("all");
  const [horizonF, setHorizonF] = useState("all");
  const [ownerF, setOwnerF] = useState("all");
  const [reviewF, setReviewF] = useState("all");
  const [sort, setSort] = useState("priority");
  const [modalOpen, setModalOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null); const [confirmId, setConfirmId] = useState(null);
  const [resched, setResched] = useState(null);

  const load = useCallback(async () => { try { setItems(await api.list("innovations")); } catch (err) { console.error("Failed to load innovations:", err); setItems([]); } }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (initialView) { setView(initialView); if (initialView === "reviewq") setSort("review"); } setStatusF(initialStatus || "all"); setShowArchived(false); }, [navKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshAll = async () => { await load(); onChange?.(); };
  const clearStatus = () => { setStatusF("all"); onClearStatus?.(); };

  const openAdd = () => { setEditing({ priority: "Medium", time_horizon: "Post-launch", status: "Idea", owner: "Steven" }); setModalOpen(true); };
  const openEdit = (r) => { setDetail(null); setEditing(r); setModalOpen(true); };
  const save = async (v) => { if (v.id) { const u = await api.update("innovations", v.id, v); toast.success("Saved"); if (detail) setDetail(u); } else { await api.create("innovations", v); toast.success("Innovation added"); } await refreshAll(); };
  const toggleArchive = async (r) => { const u = await api.update("innovations", r.id, { archived: !r.archived }); toast.success(u.archived ? "Archived" : "Restored to active"); setDetail(null); await refreshAll(); };
  const doDelete = async (reason) => { await api.remove("innovations", confirmId, reason); toast.success("Moved to Recycle Bin"); setConfirmId(null); setDetail(null); await refreshAll(); };

  const openReschedule = (i) => { setDetail(null); setResched({ idea: i, new_review_date: "", reason: "", outcome: "", explanation: "", confirmFar: false }); };
  const submitReschedule = async () => {
    const r = resched; const idea = r.idea;
    const td = today();
    if (!r.new_review_date) { toast.error("New review date is required"); return; }
    if (r.new_review_date <= td) { toast.error("New review date must be after today"); return; }
    if (r.new_review_date === idea.next_review_date) { toast.error("Pick a date different from the current review date"); return; }
    if (!r.reason.trim()) { toast.error("Reason for rescheduling is required"); return; }
    if (!r.outcome) { toast.error("Select a review outcome"); return; }
    if (r.outcome === "Other" && !r.explanation.trim()) { toast.error("Explanation is required for 'Other'"); return; }
    const days = Math.round((new Date(r.new_review_date) - new Date(td)) / 864e5);
    if (days > 180 && !r.confirmFar) { toast.error("Please confirm the date is more than 180 days away"); return; }
    try {
      const u = await api.rescheduleReview(idea.id, { new_review_date: r.new_review_date, reason: r.reason.trim(), outcome: r.outcome, explanation: r.explanation.trim() });
      toast.success(`Review rescheduled to ${r.new_review_date}`);
      setResched(null);
      if (detail && detail.id === u.id) setDetail(u);
      await refreshAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reschedule review"); }
  };
  const reschedDays = resched?.new_review_date ? Math.round((new Date(resched.new_review_date) - new Date(today())) / 864e5) : 0;

  const t = today(), soon = soonDate();

  const filtered = useMemo(() => {
    let rows = items.filter((i) => (showArchived ? i.archived : !i.archived));
    if (q) { const s = q.toLowerCase(); rows = rows.filter((i) => `${i.title} ${i.description || ""} ${i.problem_opportunity || ""}`.toLowerCase().includes(s)); }
    if (statusF !== "all") rows = rows.filter((i) => i.status === statusF);
    if (prioF !== "all") rows = rows.filter((i) => i.priority === prioF);
    if (horizonF !== "all") rows = rows.filter((i) => i.time_horizon === horizonF);
    if (ownerF !== "all") rows = rows.filter((i) => i.owner === ownerF);
    if (reviewF === "overdue") rows = rows.filter((i) => isReviewOverdue(i, t));
    else if (reviewF === "soon") rows = rows.filter((i) => isReviewSoon(i, t, soon));
    else if (reviewF === "has") rows = rows.filter((i) => !!i.next_review_date);
    else if (reviewF === "none") rows = rows.filter((i) => !i.next_review_date);
    const cmp = {
      priority: (a, b) => (PRIO_ORDER[b.priority] || 0) - (PRIO_ORDER[a.priority] || 0),
      review: (a, b) => (a.next_review_date || "9999").localeCompare(b.next_review_date || "9999"),
      updated: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      title: (a, b) => a.title.localeCompare(b.title),
      horizon: (a, b) => (HOR_ORDER[a.time_horizon] ?? 9) - (HOR_ORDER[b.time_horizon] ?? 9),
    };
    return [...rows].sort(cmp[sort]);
  }, [items, showArchived, q, statusF, prioF, horizonF, ownerF, reviewF, sort, t, soon]);

  const byStatus = useMemo(() => { const m = Object.fromEntries(INN_STATUS.map((c) => [c, []])); filtered.forEach((i) => (m[i.status] || (m[i.status] = [])).push(i)); return m; }, [filtered]);

  const reviewGroups = useMemo(() => {
    const overdue = filtered.filter((i) => isReviewOverdue(i, t));
    const soonG = filtered.filter((i) => isReviewSoon(i, t, soon));
    const ids = new Set([...overdue, ...soonG].map((i) => i.id));
    const rest = filtered.filter((i) => !ids.has(i.id));
    const scheduled = rest.filter((i) => i.next_review_date).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    const none = rest.filter((i) => !i.next_review_date).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return [
      { key: "overdue", label: "Overdue review", accent: "#DC2626", rows: overdue.sort((a, b) => (a.next_review_date || "").localeCompare(b.next_review_date || "")) },
      { key: "soon", label: "Review due soon", accent: "#E0A82E", rows: soonG.sort((a, b) => (a.next_review_date || "").localeCompare(b.next_review_date || "")) },
      { key: "scheduled", label: "Scheduled · recently updated", accent: "#1D4ED8", rows: scheduled },
      { key: "none", label: "No review date", accent: "#5A6B8C", rows: none },
    ];
  }, [filtered, t, soon]);

  const reviewChip = (i) => {
    if (isReviewOverdue(i, t)) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#DC2626]"><AlertTriangle size={12} /> Overdue {i.next_review_date}</span>;
    if (isReviewSoon(i, t, soon)) return <span className="text-xs font-semibold text-[#A16207]">Review {i.next_review_date}</span>;
    return i.next_review_date ? <span className="text-xs text-[#5A6B8C]">Review {i.next_review_date}</span> : <span className="text-xs text-[#94A3C4]">No review set</span>;
  };

  const OwnerChip = ({ owner }) => <span className="inline-flex items-center gap-1 text-xs font-medium text-[#5A6B8C]"><User size={11} /> {owner}</span>;

  const IdeaCard = ({ i, compact, onReschedule }) => (
    <div data-testid={`open-innovation-${i.id}`} role="button" tabIndex={0} onClick={() => setDetail(i)} onKeyDown={(e) => { if (e.key === "Enter") setDetail(i); }}
      className="flex w-full cursor-pointer flex-col rounded-2xl border border-[#E4E9F3] bg-white p-4 text-left shadow-[0_1px_8px_rgba(20,36,95,0.04)] transition-all hover:-translate-y-0.5 hover:border-[#7C3AED]/40 hover:shadow-md"
      style={{ borderTopColor: innStatusAccent(i.status), borderTopWidth: 3 }}>
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <h4 className="font-heading text-[15px] font-semibold leading-tight text-[#14245F]">{i.title}</h4>
        <div className="flex shrink-0 flex-col items-end gap-1"><StatusBadge status={innStatusBadge(i.status)} label={i.status} /><StatusBadge status={innPriorityBadge(i.priority)} label={i.priority} /></div>
      </div>
      <p className="mb-2 line-clamp-2 text-xs text-[#5A6B8C]">{i.description || i.problem_opportunity || "No description yet."}</p>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <OwnerChip owner={i.owner} />
        <span className="text-xs font-medium text-[#7C3AED]">{i.time_horizon}</span>
        {reviewChip(i)}
      </div>
      {!compact && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-[#EEF2FB] pt-2 text-[11px] text-[#5A6B8C]">
          <span className="flex items-center gap-1"><Clock size={11} /> Effort: <span className="text-[#14245F]">{i.estimated_effort || "—"}</span></span>
          <span className="flex items-center gap-1"><PoundSterling size={11} /> Cost: <span className="text-[#14245F]">{i.estimated_cost || "—"}</span></span>
          <span className="col-span-2 truncate">Value: <span className="text-[#14245F]">{i.strategic_value || "—"}</span></span>
          <span className="col-span-2 truncate">Benefit: <span className="text-[#14245F]">{i.user_benefit || "—"}</span></span>
          <span className="col-span-2 flex items-center gap-1 text-[#94A3C4]"><RefreshCw size={10} /> Updated {fmtDate(i.updated_at)}</span>
        </div>
      )}
      {onReschedule && (
        <div className="mt-2 border-t border-[#EEF2FB] pt-2">
          <button data-testid={`reschedule-review-${i.id}`} onClick={(e) => { e.stopPropagation(); onReschedule(); }}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#7C3AED]/30 px-3 py-1 text-xs font-semibold text-[#7C3AED] transition-colors hover:bg-[#7C3AED]/10">
            <CalIcon size={13} /> Reschedule Review
          </button>
        </div>
      )}
    </div>
  );

  return (
    <SectionCard testid="innovations-card" accent="#7C3AED" icon={Lightbulb} title="Future Innovations"
      subtitle="Your idea pipeline — capture, sequence and review future product ideas, kept separate from active module production."
      action={
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="innovations-archived-toggle" onClick={() => setShowArchived((v) => !v)} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${showArchived ? "border-[#7C3AED] bg-[#7C3AED]/10 text-[#7C3AED]" : "border-[#E4E9F3] text-[#5A6B8C]"}`}>{showArchived ? "Viewing archived" : "Active"}</button>
          {canWrite && <Button size="sm" data-testid="add-innovation-btn" onClick={openAdd} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><Plus size={15} /> Add Idea</Button>}
        </div>
      }>

      {/* View controls */}
      <div data-testid="innovations-view-controls" className="mb-4 flex flex-wrap gap-1.5 rounded-full border border-[#E4E9F3] bg-[#F7F5FF] p-1">
        {VIEWS.map(([v, label, Icon]) => (
          <button key={v} data-testid={`innovations-view-${v}`} onClick={() => setView(v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${view === v ? "bg-[#7C3AED] text-white shadow-sm" : "text-[#5A6B8C] hover:text-[#7C3AED]"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* Filters / search / sort */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3C4]" />
          <Input data-testid="innovations-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ideas…" className="h-9 border-[#E4E9F3] pl-9 text-sm" />
        </div>
        <Sel testid="innovations-filter-status" value={statusF} onChange={setStatusF} label="Status" options={INN_STATUS} />
        <Sel testid="innovations-filter-priority" value={prioF} onChange={setPrioF} label="Priority" options={INN_PRIORITY} />
        <Sel testid="innovations-filter-horizon" value={horizonF} onChange={setHorizonF} label="Horizon" options={INN_HORIZON} />
        <Sel testid="innovations-filter-owner" value={ownerF} onChange={setOwnerF} label="Owner" options={INN_OWNERS} />
        <Select value={reviewF} onValueChange={setReviewF}>
          <SelectTrigger data-testid="innovations-filter-review" className="h-9 w-auto min-w-[130px] border-[#E4E9F3] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Review: All</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="soon">Due soon</SelectItem><SelectItem value="has">Has date</SelectItem><SelectItem value="none">No date</SelectItem></SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="innovations-sort" className="h-9 w-auto min-w-[150px] border-[#E4E9F3] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="priority">Sort: Priority</SelectItem><SelectItem value="review">Sort: Review date</SelectItem><SelectItem value="updated">Sort: Last updated</SelectItem><SelectItem value="title">Sort: Title</SelectItem><SelectItem value="horizon">Sort: Time horizon</SelectItem></SelectContent>
        </Select>
      </div>

      {statusF !== "all" && (
        <div data-testid="innovations-filter-banner" className="mb-4 flex items-center justify-between rounded-xl bg-[#7C3AED]/10 px-3 py-2 text-xs font-semibold text-[#7C3AED]">
          <span>Filtered to “{statusF}” ideas</span>
          <button data-testid="clear-innovation-filter" onClick={clearStatus} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear filter</button>
        </div>
      )}

      {/* ===== ALL IDEAS ===== */}
      {view === "all" && (
        filtered.length === 0
          ? <EmptyState icon={Lightbulb} title={showArchived ? "No archived ideas" : "No ideas match"} description={showArchived ? "Archived ideas will appear here." : "Capture your first future product idea or adjust the filters."} actionLabel={showArchived ? undefined : "Add idea"} onAction={showArchived ? undefined : openAdd} />
          : <div data-testid="innovations-all" className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">{filtered.map((i) => <IdeaCard key={i.id} i={i} />)}</div>
      )}

      {/* ===== KANBAN ===== */}
      {view === "kanban" && (
        <div className="-mx-1 flex snap-x gap-4 overflow-x-auto px-1 pb-3 [scrollbar-width:thin]" data-testid="innovations-kanban">
          {INN_STATUS.map((col) => (
            <div key={col} data-testid={`innovations-col-${col}`} className="flex w-72 shrink-0 snap-start flex-col rounded-xl border border-[#E4E9F3] bg-[#F7F9FE] p-3">
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.06em] text-[#5A6B8C]"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: innStatusAccent(col) }} />{col}</span>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-[#5A6B8C] ring-1 ring-[#E4E9F3]">{(byStatus[col] || []).length}</span>
              </div>
              <div className="space-y-2">
                {(byStatus[col] || []).length === 0 ? <p className="rounded-lg border border-dashed border-[#D8E0F0] py-4 text-center text-[11px] text-[#B7C1DA]">Empty</p> : (byStatus[col] || []).map((i) => <IdeaCard key={i.id} i={i} compact />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== ROADMAP ===== */}
      {view === "roadmap" && (() => {
        const committed = filtered.filter((r) => INN_ROADMAP_STATUSES.includes(r.status));
        return (
          <div data-testid="innovations-roadmap">
            <p className="mb-4 text-sm text-[#5A6B8C]">Committed ideas (Planned &amp; On roadmap) sequenced by time horizon — nearest first.</p>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
              {INN_HORIZON.map((h) => {
                const rows = committed.filter((r) => r.time_horizon === h).sort((a, b) => (a.next_review_date || "9999").localeCompare(b.next_review_date || "9999"));
                const accent = innHorizonAccent(h);
                return (
                  <div key={h} data-testid={`roadmap-lane-${h}`} className="flex-1 rounded-2xl border border-[#E4E9F3] bg-[#F7F9FE] p-3">
                    <div className="mb-3 rounded-xl px-3 py-2 text-white" style={{ backgroundColor: accent }}>
                      <p className="text-sm font-bold">{h}</p>
                      <p className="text-[11px] opacity-90">{innHorizonBlurb(h)} · {rows.length} {rows.length === 1 ? "idea" : "ideas"}</p>
                    </div>
                    <div className="space-y-2">
                      {rows.length === 0 ? <p className="rounded-lg border border-dashed border-[#D8E0F0] px-3 py-6 text-center text-xs text-[#94A3C4]">Nothing scheduled</p> : rows.map((i) => <IdeaCard key={i.id} i={i} compact />)}
                    </div>
                  </div>
                );
              })}
            </div>
            {committed.length === 0 && <p className="mt-4 rounded-xl bg-[#F7F5FF] px-4 py-3 text-center text-sm text-[#7C3AED]">No committed ideas yet. Move an idea to <strong>Planned</strong> or <strong>On roadmap</strong> to place it on the timeline.</p>}
          </div>
        );
      })()}

      {/* ===== REVIEW QUEUE ===== */}
      {view === "reviewq" && (
        filtered.length === 0
          ? <EmptyState icon={CalendarClock} title="Nothing to review" description="No ideas match the current filters." />
          : <div className="space-y-5" data-testid="innovations-reviewq">
            {reviewGroups.filter((g) => g.rows.length > 0).map((g) => (
              <div key={g.key} data-testid={`reviewq-group-${g.key}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: g.accent }} />
                  <h4 className="text-sm font-bold uppercase tracking-[0.06em] text-[#14245F]">{g.label}</h4>
                  <span className="rounded-full bg-[#EEF2FB] px-2 py-0.5 text-xs font-semibold text-[#5A6B8C]">{g.rows.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">{g.rows.map((i) => <IdeaCard key={i.id} i={i} onReschedule={canWrite && g.key !== "none" && !["Rejected", "Delivered"].includes(i.status) && i.next_review_date ? () => openReschedule(i) : undefined} />)}</div>
              </div>
            ))}
          </div>
      )}

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Innovation" : "Add Innovation"} submitLabel={editing?.id ? "Save Changes" : "Add Innovation"} fields={innovationFields()} initialValues={editing} onSubmit={save} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this innovation?" onConfirm={doDelete} softDelete />

      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>
        <DialogContent data-testid="reschedule-modal" className="max-h-[92vh] max-w-md overflow-y-auto border-[#E4E9F3] bg-white">
          {resched && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading text-lg text-[#14245F]"><CalIcon size={18} className="text-[#7C3AED]" /> Reschedule Review</DialogTitle>
              <DialogDescription className="text-[#5A6B8C]">{resched.idea.title} · current review {resched.idea.next_review_date || "—"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-[#5A6B8C]">New review date *</Label>
                <Input type="date" data-testid="reschedule-date" value={resched.new_review_date} onChange={(e) => setResched({ ...resched, new_review_date: e.target.value, confirmFar: false })} className="border-[#E4E9F3]" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-[#5A6B8C]">Review outcome *</Label>
                <Select value={resched.outcome} onValueChange={(v) => setResched({ ...resched, outcome: v })}>
                  <SelectTrigger data-testid="reschedule-outcome" className="border-[#E4E9F3]"><SelectValue placeholder="Select an outcome" /></SelectTrigger>
                  <SelectContent>{INN_REVIEW_OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {resched.outcome === "Other" && (
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-[#5A6B8C]">Explanation *</Label>
                  <Textarea data-testid="reschedule-explanation" value={resched.explanation} onChange={(e) => setResched({ ...resched, explanation: e.target.value })} rows={2} className="border-[#E4E9F3]" placeholder="Explain the outcome" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-[#5A6B8C]">Reason for rescheduling *</Label>
                <Textarea data-testid="reschedule-reason" value={resched.reason} onChange={(e) => setResched({ ...resched, reason: e.target.value })} rows={2} className="border-[#E4E9F3]" placeholder="Why is the review moving?" />
              </div>
              {reschedDays > 90 && reschedDays <= 180 && (
                <p data-testid="reschedule-warn-90" className="flex items-center gap-1.5 rounded-lg bg-[#E0A82E]/12 px-3 py-2 text-xs font-medium text-[#A16207]"><AlertTriangle size={13} /> That's {reschedDays} days away — a long time between reviews.</p>
              )}
              {reschedDays > 180 && (
                <label data-testid="reschedule-confirm-180" className="flex items-start gap-2 rounded-lg bg-[#DC2626]/10 px-3 py-2 text-xs font-medium text-[#B91C1C]">
                  <Checkbox checked={resched.confirmFar} onCheckedChange={(c) => setResched({ ...resched, confirmFar: !!c })} className="mt-0.5" />
                  <span>This is {reschedDays} days away (over 180). I confirm this is intentional.</span>
                </label>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResched(null)} className="border-[#E4E9F3]">Cancel</Button>
              <Button data-testid="reschedule-submit" onClick={submitReschedule} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]">Reschedule Review</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="innovation-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto border-[#E4E9F3] bg-white">
          {detail && (<>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={innStatusBadge(detail.status)} label={detail.status} />
                <StatusBadge status={innPriorityBadge(detail.priority)} label={detail.priority} />
                <span className="text-xs text-[#5A6B8C]">{detail.time_horizon} · {detail.owner}</span>
                {detail.archived && <StatusBadge status="neutral" label="Archived" />}
              </div>
              <DialogTitle className="mt-2 font-heading text-xl text-[#14245F]">{detail.title}</DialogTitle>
              <DialogDescription className="text-[#5A6B8C]">{reviewChip(detail)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm text-[#14245F]">
              {detail.problem_opportunity && <p><span className="text-[#5A6B8C]">Problem / opportunity: </span>{detail.problem_opportunity}</p>}
              {detail.description && <p><span className="text-[#5A6B8C]">Description: </span>{detail.description}</p>}
              {detail.user_benefit && <p><span className="text-[#5A6B8C]">User benefit: </span>{detail.user_benefit}</p>}
              {detail.strategic_value && <p><span className="text-[#5A6B8C]">Strategic value: </span>{detail.strategic_value}</p>}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg bg-[#F7F9FE] p-3">
                <span><span className="text-[#5A6B8C]">Effort: </span>{detail.estimated_effort || "—"}</span>
                <span><span className="text-[#5A6B8C]">Cost: </span>{detail.estimated_cost || "—"}</span>
              </div>
              {detail.dependencies && <p><span className="text-[#5A6B8C]">Dependencies: </span>{detail.dependencies}</p>}
              {detail.risks && <p><span className="text-[#5A6B8C]">Risks: </span>{detail.risks}</p>}
              {detail.evidence_feedback && <p><span className="text-[#5A6B8C]">Evidence / feedback: </span>{detail.evidence_feedback}</p>}
              {detail.notes && <p><span className="text-[#5A6B8C]">Notes: </span>{detail.notes}</p>}
              <p className="text-xs text-[#94A3C4]">Created {fmtDate(detail.created_at)} · Last updated {fmtDate(detail.updated_at)}</p>
            </div>
            {canWrite && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-[#E4E9F3] pt-4">
              <Button data-testid="innovation-edit" onClick={() => openEdit(detail)} className="bg-[#7C3AED] text-white hover:bg-[#6D28D9]"><Pencil size={15} /> Edit</Button>
              <Button variant="outline" data-testid="innovation-archive" onClick={() => toggleArchive(detail)} className="border-[#E4E9F3]">{detail.archived ? <><ArchiveRestore size={15} /> Restore</> : <><Archive size={15} /> Archive</>}</Button>
              <Button variant="outline" data-testid="innovation-delete" onClick={() => setConfirmId(detail.id)} className="border-[#E4E9F3] text-[#B91C1C] hover:bg-[#DC2626]/10"><Trash2 size={15} /> Delete</Button>
            </div>
            )}
            <div className="mt-4 border-t border-[#E4E9F3] pt-4">
              <UpdatesHistory relatedId={detail.id} relatedType="Innovation" relatedName={detail.title} area="Product" title="Dated Updates" />
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
